import Add from '@mui/icons-material/Add';
import ArrowBackIosNew from '@mui/icons-material/ArrowBackIosNew';
import Save from '@mui/icons-material/Save';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CardContent from '@mui/material/CardContent';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { pick } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';

import DataGridPager from '~components/DataGrid/DataGridPager';
import DataTable from '~components/DataTable/DataTable';
import { SmallLoadingButton } from '~components/Order/ordersDispatchStyledComponents';
import { RutterConnection } from '~hooks/useRutter/models';
import useCreateRutterConnection from '~hooks/useRutter/useCreateRutterConnection';
import useRutterConnections, {
  RUTTER_CONNECTIONS_FIRST_PAGE,
} from '~hooks/useRutter/useRutterConnections';
import RutterDisconnectDialog from '~pages/Settings/Integrations/RutterDisconnectDialog';
import {
  actionsColDef,
  lastSyncColDef,
  platformColDef,
  RutterManagementRow,
  statusColDef,
} from '~pages/Settings/Integrations/rutterManagementDataGridColumnsDefinition';
import { ROUTES as routes } from '~router/routePaths';
import { Pagination } from '~services/pagination';
import { useStores } from '~store';
import { Nullable } from '~types/Nullable';

const RutterManagement = observer(() => {
  return <RutterManagementAccess component={RutterManagementGrid} />;
});

const RutterManagementGrid = observer(() => {
  const theme = useTheme();
  const navigate = useNavigate();

  const { rutterIntegrationStore } = useStores();
  const { fetchConnections } = useRutterConnections();
  const pagination = useRef<Pagination>({ ...RUTTER_CONNECTIONS_FIRST_PAGE });

  const [isDisconnectDialogOpen, setIsDisconnectDialogOpen] = useState(false);
  const [selectedConnection, setSelectedConnection] =
    useState<Nullable<RutterConnection>>(null);
  const { createRutterConnection, relinkRutterConnection } = useCreateRutterConnection({
    onLinked: () => {
      fetchCurrentPage();
    },
  });
  // we only support one connection per company
  const hasExistingConnection = (rutterIntegrationStore.connections ?? []).length > 0;

  const isDeletingConnection = JSON.stringify(
    Object.fromEntries(rutterIntegrationStore.isDeletingConnection),
  );

  const fetchCurrentPage = async () => {
    const response = await fetchConnections(pagination.current);

    if (response) {
      pagination.current = {
        ...pagination.current,
        before: '',
        after: '',
        ...response.pagination,
      };
    }
  };

  const fetchPreviousPage = () => {
    pagination.current = pick(pagination.current, ['page', 'limit', 'before']);
    pagination.current.page -= 1;

    fetchCurrentPage();
  };

  const fetchNextPage = () => {
    pagination.current = pick(pagination.current, ['page', 'limit', 'after']);
    pagination.current.page += 1;

    fetchCurrentPage();
  };

  const fetchFirstPage = () => {
    pagination.current = { ...pick(pagination.current, ['limit']), page: 1 };

    fetchCurrentPage();
  };

  const columns = useMemo(
    () => [platformColDef, statusColDef, lastSyncColDef, actionsColDef],
    [],
  );

  const rows = useMemo(() => {
    return (rutterIntegrationStore.connections ?? []).map((connection) => {
      const rows: RutterManagementRow = {
        id: connection.id,
        isDeleting: rutterIntegrationStore.isDeletingConnection.get(connection.id),
        connection,
        onDelete: () => {
          setIsDisconnectDialogOpen(true);
          setSelectedConnection(connection);
        },
      };

      return rows;
    });
  }, [JSON.stringify(rutterIntegrationStore.connections), isDeletingConnection]);

  useEffect(() => {
    fetchCurrentPage();
  }, []);

  return (
    <Box
      sx={{
        maxWidth: '1000px',
      }}
    >
      <Button
        color="secondary"
        component={Link}
        startIcon={<ArrowBackIosNew fontSize="small" />}
        to={`/${routes.settings.base}/${routes.settings.integrations.base}`}
        variant="text"
      >
        {t('administration.integration.title')}
      </Button>
      <CardContent
        sx={{
          paddingX: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="h4">
            {t(`administration.integration.rutter.name`)}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {hasExistingConnection && (
              <Typography
                variant="body1"
                sx={{
                  color: theme.brandV2.colors.treadGray3,
                  mr: 1,
                }}
              >
                {t('administration.integration.rutter.cta.unable_to_link')}
              </Typography>
            )}
            <SmallLoadingButton
              color="brandV2Yellow"
              disabled={hasExistingConnection}
              loading={rutterIntegrationStore.isSavingConnection}
              onClick={createRutterConnection}
              startIcon={rutterIntegrationStore.isSavingConnection ? <Save /> : <Add />}
            >
              {t('administration.integration.rutter.cta.link_an_account')}
            </SmallLoadingButton>
          </Box>
        </Box>
      </CardContent>
      <DataTable
        id="rutter-management-grid"
        columns={columns}
        headerTrailingComponent={
          <DataGridPager
            isFetching={rutterIntegrationStore.isLoadingConnections}
            onNavigate={(link) => {
              if (link === 'after') {
                fetchNextPage();
              } else if (link === 'before') {
                fetchPreviousPage();
              } else {
                fetchFirstPage();
              }
            }}
            pagination={pagination.current}
          />
        }
        loading={rutterIntegrationStore.isLoadingConnections}
        rows={rows}
        onRowClick={(row) => {
          const rowConnection = (rutterIntegrationStore.connections ?? []).find(
            (candidate) => candidate.id === row.id,
          );
          // a connection with no status has not completed Rutter Link; reopen the
          // popup instead of navigating to an empty mapping page
          if (rowConnection && !rowConnection.status && rowConnection.linkUrl) {
            relinkRutterConnection(rowConnection);
            return;
          }

          navigate(
            `${routes.settings.integrations.rutter.quickBooks}?connection_id=${row.id}`,
          );
        }}
      />
      <RutterDisconnectDialog
        connection={selectedConnection}
        isOpen={isDisconnectDialogOpen}
        onClose={() => {
          setIsDisconnectDialogOpen(false);
          setSelectedConnection(null);
        }}
        onDisconnected={() => {
          setIsDisconnectDialogOpen(false);
          setSelectedConnection(null);
        }}
      />
    </Box>
  );
});

interface RutterQuickBooksOnlineProps {
  component: React.ComponentType;
}

export const RutterManagementAccess = observer(
  ({ component: Component }: RutterQuickBooksOnlineProps) => {
    const { userStore } = useStores();

    const userCanManageAccountingIntegration =
      userStore.getPermissions().canManageAccountingIntegration;

    if (!userCanManageAccountingIntegration) {
      return <Navigate to={routes.base} />;
    }

    return <Component />;
  },
);

export default RutterManagement;
